/**
 * PublicMunicipality — alinhada ao design de tectur.pt
 *
 * Paleta e tipografia batem 1:1 com o site institucional:
 *   • Brand orange #FF6B1A
 *   • Dark bg #0F0D0B / secondary #1A1613
 *   • Hero com background image + overlay + pretitle + h1 com <span class="accent">
 *   • Sections: .container (max 1200) + .section-header (h2 + underline + muted)
 *   • Cards: image-top + body simples
 *   • Botão pill orange (.btn) + variante outline (.btn-outline)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Mountain, Loader2, MapPin, Building2, Route as RouteIcon, ArrowRight, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = {
  bg: '#0F0D0B',
  bg2: '#1A1613',
  bgAlt: '#181410',
  brand: '#FF6B1A',
  ink: '#F2EBDD',
  inkMuted: '#8A7A63',
  line: 'rgba(255,255,255,0.08)',
  cardBg: '#1F1A16',
};

const kmFmt = (n) => { const v = Number(n) || 0; return v < 1 ? v.toFixed(2) : v.toFixed(1); };
const fontStack = "'Inter', system-ui, -apple-system, sans-serif";

const Btn = ({ children, href, onClick, outline, testid, target }) => (
  <a
    href={href} target={target} onClick={onClick} rel="noopener noreferrer"
    data-testid={testid}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '12px 24px', borderRadius: 999,
      fontSize: 14, fontWeight: 700, letterSpacing: '.5px',
      textTransform: 'uppercase', textDecoration: 'none',
      backgroundColor: outline ? 'transparent' : C.brand,
      color: outline ? C.ink : '#fff',
      border: outline ? `2px solid ${C.ink}` : '2px solid transparent',
      cursor: 'pointer', transition: 'transform .15s',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
    onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
  >{children}</a>
);

const RouteCard = ({ route }) => (
  <Link
    to={`/r/${route.route_id}`} target="_blank" rel="noopener noreferrer"
    style={{
      display: 'block', textDecoration: 'none', color: C.ink,
      backgroundColor: C.cardBg, borderRadius: 8, overflow: 'hidden',
      border: `1px solid ${C.line}`, transition: 'transform .2s, box-shadow .2s',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,.5)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
    data-testid={`public-route-card-${route.route_id}`}
  >
    <div style={{
      height: 200,
      background: route.cover_image_path
        ? `linear-gradient(rgba(15,13,11,.2), rgba(15,13,11,.7)), url(${API}/routes/${route.route_id}/cover) center/cover`
        : `linear-gradient(135deg, #3D362E, ${C.bg2})`,
      position: 'relative',
    }}>
      {route.difficulty && (
        <span style={{
          position: 'absolute', top: 12, right: 12,
          padding: '4px 10px', borderRadius: 999,
          backgroundColor: C.brand, color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: '.5px',
        }}>{route.difficulty.toUpperCase()}</span>
      )}
    </div>
    <div style={{ padding: '20px 22px' }}>
      <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, marginBottom: 8, letterSpacing: '-.3px' }}>{route.name}</h3>
      {route.region && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.inkMuted, marginBottom: 10 }}>
          <MapPin size={12} /> {route.region}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 13, color: C.inkMuted, marginBottom: 12 }}>
        {route.distance_km != null && <span>📏 <b style={{ color: C.ink }}>{kmFmt(route.distance_km)} km</b></span>}
        {route.elevation_gain_m > 0 && <span>⛰️ <b style={{ color: C.ink }}>+{Math.round(route.elevation_gain_m)} m</b></span>}
        {route.duration_hours != null && <span>⏱ <b style={{ color: C.ink }}>{Number(route.duration_hours).toFixed(1)}h</b></span>}
        {route.pois_count > 0 && <span>📍 <b style={{ color: C.ink }}>{route.pois_count} POIs</b></span>}
      </div>
      {route.description && (
        <p style={{ fontSize: 13, color: C.inkMuted, lineHeight: 1.5, margin: 0, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {route.description}
        </p>
      )}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: C.brand, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px' }}>
        Ver rota <ArrowRight size={14} />
      </span>
    </div>
  </Link>
);

const HeroSlides = ({ routes }) => {
  const slides = useMemo(() => {
    const out = [];
    routes.forEach((r) => {
      if (r.cover_image_path) out.push({ url: `${API}/routes/${r.route_id}/cover`, name: r.name });
      for (let i = 0; i < Math.min(r.photos_count || 0, 3); i += 1) {
        out.push({ url: `${API}/routes/${r.route_id}/photos/${i}?w=1600`, name: r.name });
      }
    });
    return out.slice(0, 12);
  }, [routes]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  if (slides.length === 0) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }} data-testid="hero-slides">
      {slides.map((s, i) => (
        <div
          key={s.url}
          style={{
            position: 'absolute', inset: 0,
            background: `url(${s.url}) center/cover no-repeat`,
            opacity: i === idx ? 1 : 0,
            transform: i === idx ? 'scale(1.06)' : 'scale(1)',
            transition: 'opacity 1.5s ease, transform 7s ease-out',
          }}
        />
      ))}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(rgba(0,0,0,.45), rgba(15,13,11,.92))' }} />
      <div
        style={{ position: 'absolute', left: 20, bottom: 16, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.ink, opacity: .85 }}
        data-testid="hero-slide-caption"
      >
        <MapPin size={12} style={{ color: C.brand }} /> {slides[idx]?.name}
      </div>
    </div>
  );
};

const PublicMunicipality = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/municipalities/${slug}`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 404 ? 'Município não encontrado' : 'Erro a carregar município');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: C.bg }}>
        <Loader2 style={{ width: 32, height: 32, color: C.brand, animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 32, backgroundColor: C.bg, color: C.ink, fontFamily: fontStack }}>
        <Mountain size={64} style={{ color: C.inkMuted }} />
        <h1 style={{ fontSize: 28, fontWeight: 800 }}>{error}</h1>
        <a href="https://tectur.pt/" style={{ color: C.brand, textDecoration: 'underline' }}>Voltar a Tectur.pt</a>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: C.bg, color: C.ink, fontFamily: fontStack, minHeight: '100vh' }} data-testid="public-municipality-page">
      {/* HERO */}
      <section style={{
        position: 'relative',
        background: `linear-gradient(rgba(0,0,0,.5), rgba(0,0,0,.85)), linear-gradient(135deg, #3D362E, ${C.bg2})`,
        padding: '44px 20px 40px', textAlign: 'center', overflow: 'hidden',
      }}>
        <HeroSlides routes={data.routes} />
        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
          <div style={{
            width: 76, height: 76, margin: '0 auto 14px',
            borderRadius: 12, backgroundColor: C.cardBg, border: `3px solid ${C.brand}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
          }}>
            {data.has_logo
              ? <img src={`${API}/municipalities/${data.municipality_id}/logo`} alt={data.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }} />
              : <Building2 size={36} style={{ color: C.brand }} />}
          </div>
          <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 700, color: C.brand, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>
            Município · Tectur 4×4
          </span>
          <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 900, margin: '0 0 10px', letterSpacing: '-1px', lineHeight: 1.1 }}>
            {data.name}<br />
            <span style={{ color: C.brand, fontSize: '.6em', fontWeight: 700 }}>
              {data.district ? `DISTRITO DE ${data.district.toUpperCase()}` : ''}
            </span>
          </h1>
          <p style={{ fontSize: 15, color: C.inkMuted, maxWidth: 640, margin: '0 auto 18px' }}>
            {data.routes_count} rota{data.routes_count === 1 ? '' : 's'} 4×4 dispon{data.routes_count === 1 ? 'ível' : 'íveis'} na app Tectur 4×4 Navigation para este concelho.
          </p>
          <Btn href="https://play.google.com/store/apps/details?id=pt.tectur.navigation" target="_blank" testid="hero-cta-app">
            Instalar App <ExternalLink size={14} />
          </Btn>
        </div>
      </section>

      {/* SECTION — ROTAS */}
      <section style={{ padding: '80px 20px', backgroundColor: C.bgAlt }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 900, margin: 0, letterSpacing: '-.5px' }}>
              Rotas 4×4 em <span style={{ color: C.brand }}>{data.name}</span>
            </h2>
            <div style={{ width: 60, height: 3, backgroundColor: C.brand, margin: '16px auto 12px', borderRadius: 2 }} />
            <p style={{ fontSize: 16, color: C.inkMuted, margin: 0 }}>
              Rotas testadas no terreno · nunca tracks aleatórios
            </p>
          </div>

          {data.routes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '64px 20px', color: C.inkMuted }}>
              <RouteIcon size={64} style={{ opacity: .4, margin: '0 auto 16px' }} />
              <p style={{ fontSize: 16 }}>Ainda não há rotas públicas para este município.</p>
              <p style={{ fontSize: 13, marginTop: 8 }}>Volta em breve — estamos sempre a acrescentar rotas testadas.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 24 }}>
              {data.routes.map((r) => <RouteCard key={r.route_id} route={r} />)}
            </div>
          )}
        </div>
      </section>

      {/* BANNER — Parceria Municípios */}
      <section style={{
        padding: '60px 20px',
        background: `linear-gradient(rgba(0,0,0,.7), rgba(0,0,0,.9)), linear-gradient(135deg, #3D362E, ${C.bg2})`,
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 700, color: C.brand, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>
            Setor Público
          </span>
          <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 900, margin: '0 0 16px', letterSpacing: '-.3px' }}>
            Parceria com o teu Município
          </h2>
          <p style={{ fontSize: 16, color: C.inkMuted, marginBottom: 24, lineHeight: 1.6 }}>
            Criamos rotas turísticas oficiais para o teu concelho e integramo-las na app Tectur 4×4. Turismo qualificado, público segmentado, dados reais de utilização.
          </p>
          <Btn href="https://tectur.pt/municipios" target="_blank" testid="cta-partner">Saber Mais</Btn>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: '32px 20px', borderTop: `1px solid ${C.line}`, backgroundColor: C.bg }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Mountain size={24} style={{ color: C.brand }} />
            <span style={{ fontWeight: 900, letterSpacing: '.5px' }}>TECTUR 4×4 NAVIGATION</span>
          </div>
          <p style={{ fontSize: 12, color: C.inkMuted, margin: 0 }}>
            © {new Date().getFullYear()} Tectur — Turismo Off-Road em Portugal ·{' '}
            <a href="https://tectur.pt/" target="_blank" rel="noopener noreferrer" style={{ color: C.brand, textDecoration: 'none' }}>tectur.pt</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicMunicipality;
